import { UserDTO } from '../../api';
import CRMApi, { apiHost } from '../request';

const request = new CRMApi();

export const getCompany = (id: I.ID) =>
  request.get<I.Company>(`/user/company/${id}`);

export const getCurrentUser = () => {
  return request.get<I.User>(`/user/current`);
};

export const getSystemConfig = () => {
  return request.get<I.SystemConfig>('/user/system/config');
};

export const login = (
  companyId: I.ID,
  data: { username: string; password: string; code: string },
) => {
  const form = new FormData();
  form.set('companyId', companyId.toString());
  Object.entries(data).forEach(([key, value]) => {
    form.set(key, value);
  });
  return fetch(`${apiHost}/user/login`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  }).then((res) => {
    if (res.status !== 200) {
      return res.json().then((data) => {
        throw new Error(data.message || '登录失败');
      });
    }
  });
};

export const listSystemUser = (
  companyId: I.ID,
  data?: {
    name?: string;
    roleId?: I.ID;
    departmentId?: I.ID;
    userStates?: I.UserState;
  } & I.PageParams,
) => {
  const { current, pageSize, ...bodyData } = data || {};
  return request.post<I.PageResult<UserDTO>>(`/company/${companyId}/user/list`, {
    params: {
      current,
      pageSize,
    },
    data: bodyData,
  });
};

export const listRole = (companyId: I.ID) => {
  return request.get<I.Role[]>(`/company/${companyId}/role`);
};

export const addRole = (
  companyId: I.ID,
  data: { name: string; description?: string },
) => {
  return request.post(`/company/${companyId}/role`, { data });
};

export const updateRole = (
  companyId: I.ID,
  roleId: I.ID,
  data: { name: string; description?: string },
) => {
  return request.put(`/company/${companyId}/role/${roleId}`, { data });
};

export const listRolePermission = (companyId: I.ID, roleId: I.ID) => {
  return request.get<I.RolePermission[]>(
    `/company/${companyId}/role/${roleId}/permission`,
  );
};

export const updateRolePermission = (
  companyId: I.ID,
  roleId: I.ID,
  data: string[],
) => {
  return request.post(`/company/${companyId}/role/${roleId}/permission`, {
    data,
  });
};

export const listDepartment = (companyId: I.ID) => {
  return request.get<I.Department[]>(`/company/${companyId}/department`);
};

export const addUser = (
  companyId: I.ID,
  data: { name: string; mobile: string; roleId: I.ID; departmentId?: I.ID },
) => {
  return request.post(`/company/${companyId}/user`, { data });
};

export const updateUser = (
  companyId: I.ID,
  id: I.ID,
  data: {
    name: string;
    mobile: string;
    reoleId?: I.ID;
    departmentId?: I.ID;
    state: I.UserState;
  },
) => {
  return request.put(`/company/${companyId}/user/${id}`, { data });
};

export const addDepartment = (
  companyId: I.ID,
  data: {
    name: string;
    descripton?: string;
    parentId?: I.ID;
  },
) => {
  return request.post(`/company/${companyId}/department`, {
    data,
  });
};

export const updateDepartment = (
  companyId: I.ID,
  depId: I.ID,
  data: {
    name: string;
    descriptuon?: string;
    parentId?: string;
  },
) => {
  return request.put(`/company/${companyId}/department/${depId}`, {
    data,
  });
};

// ==================== 审计相关接口 ====================

export interface BaseUserDTO {
  id: number;
  name: string;
  mobile: string;
  state: number;
  roleId: number;
  departmentId: number;
}

export interface UserSmsDTO {
  id: number;
  companyId: number;
  msgId: string;
  userId: number;
  address: string;
  type: number; // 1-接收，2-发送
  body: string;
  smsedAt: string;
  createdAt: string;
  updatedAt: string;
  user: BaseUserDTO;
}

export interface WechatUserDTO {
  id: number;
  companyId: number;
  userId: number;
  wxid: string;
  avatar: string;
  nickname: string;
  raw: string;
  createdAt: string;
  updatedAt: string;
  user: BaseUserDTO;
}

export interface WechatContactDTO {
  id: number;
  userId: number;
  wxid: string;
  username: string;
  nickname: string;
  type: number;
  alias: string;
  avatar: string;
  raw: string;
  createTime: string;
  createdAt: string;
  updatedAt: string;
  user: BaseUserDTO;
}

export interface WechatChatroomDTO {
  id: number;
  userId: number;
  wxid: string;
  chatroomnick: string;
  chatroomname: string;
  memberlist: string;
  displayname: string;
  roomowner: string;
  addtime: string;
  raw: string;
  createdAt: string;
  updatedAt: string;
  user: BaseUserDTO;
}

export interface WechatMessageDTO {
  id: number;
  userId: number;
  wxid: string;
  createTime: string;
  msgId: number;
  msgSvrId: number;
  content: string;
  type: number;
  msgSeq: number;
  talker: string;
  talkerId: number;
  status: number;
  imgPath: string;
  isSend: number; // 0-接收，1-发送
  raw: string;
  createdAt: string;
  updatedAt: string;
  user: BaseUserDTO;
}

export interface UserFileDTO {
  id: number;
  userId: number;
  filename: string;
  size: number;
  contentType: string;
  date: string;
  createdAt: string;
  updatedAt: string;
}

export interface WechatFileDTO {
  id: number;
  userId: number;
  wxid: string;
  fileId: number;
  msgSvrId: number;
  imgPath: string;
  key: string;
  createdAt: string;
  updatedAt: string;
  user: BaseUserDTO;
  userFile: UserFileDTO;
}

// 1. 查询用户短信地址列表
export const getUserSmsAddresses = (companyId: I.ID, userId: I.ID) => {
  return request.get<string[]>(`/company/${companyId}/audit/user_sms/addresses`, {
    params: { userId },
  });
};

// 2. 查询用户与指定地址的短信记录
export const getUserSmsMessages = (
  companyId: I.ID,
  userId: I.ID,
  address: string,
) => {
  return request.get<UserSmsDTO[]>(`/company/${companyId}/audit/user_sms/messages`, {
    params: { userId, address },
  });
};

// 3. 查询用户的微信账号列表
export const getWechatUsers = (companyId: I.ID, userId: I.ID) => {
  return request.get<WechatUserDTO[]>(`/company/${companyId}/audit/wechat_user`, {
    params: { userId },
  });
};

// 4. 查询微信联系人列表
export const getWechatContacts = (
  companyId: I.ID,
  userId: I.ID,
  wxid: string,
) => {
  return request.get<WechatContactDTO[]>(`/company/${companyId}/audit/wechat_contact`, {
    params: { userId, wxid },
  });
};

// 5. 查询微信群组列表
export const getWechatChatrooms = (
  companyId: I.ID,
  userId: I.ID,
  wxid: string,
) => {
  return request.get<WechatChatroomDTO[]>(`/company/${companyId}/audit/wechat_chatroom`, {
    params: { userId, wxid },
  });
};

// 6. 查询微信消息记录（分页）
export const getWechatMessages = (
  companyId: I.ID,
  userId: I.ID,
  wxid: string,
  talker: string,
  createTime?: number,
) => {
  return request.get<WechatMessageDTO[]>(`/company/${companyId}/audit/wechat_message`, {
    params: { userId, wxid, talker, createTime },
  });
};

// 7. 批量查询微信文件
export const getWechatFiles = (
  companyId: I.ID,
  userId: I.ID,
  wxid: string,
  msgSvrIds: string[],
) => {
  return request.get<WechatFileDTO[]>(`/company/${companyId}/audit/wechat_file`, {
    params: { companyId, userId, wxid, msgSvrIds },
  });
};